import React from 'react';
import { useAppDispatch } from '../app/hooks';
import { setMapCenter } from '../slices/mapCenter';
import { useAppSelector } from '../app/hooks';
import SubSection from './SubSection';
import { DELIVERY_ADDRESS_TYPE } from '../libs/polygon-ordering/src/constants/deliveryAddress';
import { setSelectedAddress } from '../slices/deliveryAddressForm';
import { TEXT_PROPERTIES } from '../utils/theme';
import getThemeLookup from '../selectors/getThemeLookup';
import RedcatMapPinHomeIcon from './icons/redcat/RedcatMapPinHomeIcon';
import combineStyles from '../utils/combineStyles';
import RedcatMapPinIcon from './icons/redcat/RedcatMapPinIcon';
import RedcatBriefcaseIcon from './icons/redcat/RedcatBriefcaseIcon';
import RedcatHistoryIcon from './icons/redcat/RedcatHistoryIcon';
import RedcatCheckIcon from './icons/redcat/RedcatCheckIcon';
import RedcatMapPinGroupIcon from './icons/redcat/RedcatMapPinGroupIcon';
import RedcatMapPinVariantIcon from './icons/redcat/RedcatMapPinVariantIcon';

const AddressItem = ({ address }: { address: DeliveryAddress }) => {
  const p = useAppSelector(getThemeLookup);
  const dispatch = useAppDispatch();

  const formState = useAppSelector(state => state.deliveryAddressForm);

  const iconColor = p('savedAddressIcon', ['color']).color;

  if (!address || !address.displayAddress) {
    return null;
  }

  const getTitle = () => {
    if (address.addressType === DELIVERY_ADDRESS_TYPE.HOME) {
      return 'Home';
    }

    if (address.addressType === DELIVERY_ADDRESS_TYPE.WORK) {
      return 'Work';
    }

    if (address.locationName) {
      return address.locationName;
    }

    return address.displayAddress.firstLine;
  };

  const getSubtitle = () => {
    if (
      address.addressType === DELIVERY_ADDRESS_TYPE.HOME ||
      address.addressType === DELIVERY_ADDRESS_TYPE.WORK ||
      address.locationName
    ) {
      return address.displayAddress.firstLine;
    }

    return address.displayAddress.secondLine;
  };

  const getRightIcon = () => {
    if (isActive()) {
      const color = p('addressCheckIcon', ['color']).color;
      return <RedcatCheckIcon color={color} />;
    }

    switch (address.addressType) {
      case DELIVERY_ADDRESS_TYPE.HOME:
        return <RedcatMapPinHomeIcon color={iconColor} />;
      case DELIVERY_ADDRESS_TYPE.WORK:
        return <RedcatBriefcaseIcon color={iconColor} />;
      case DELIVERY_ADDRESS_TYPE.FRIENDS_FAMILY:
        return <RedcatMapPinGroupIcon color={iconColor} />;
      case DELIVERY_ADDRESS_TYPE.OTHER:
        return <RedcatMapPinVariantIcon color={iconColor} />;
    }

    if (formState.addressInput) {
      return null;
    }

    if (!address.addressId) {
      return <RedcatHistoryIcon color={iconColor} />;
    }

    return null;
  };

  const onAddressClicked = () => {
    dispatch(setSelectedAddress(address));
    if (address.location) {
      dispatch(setMapCenter(address.location));
    }
  };

  const isActive = () => {
    if (address?.addressId) {
      return formState.selectedAddress?.addressId === address.addressId;
    }

    if (address.addressId && !formState.selectedAddress?.addressId) {
      return (
        address.addressType === formState.selectedAddress?.addressType &&
        address.displayAddress.firstLine === formState.selectedAddress?.displayAddress.firstLine &&
        address.displayAddress.secondLine === formState.selectedAddress?.displayAddress.secondLine
      );
    }

    return formState.selectedAddress?.placeId === address.placeId;
  };

  return (
    <SubSection
      title={getTitle()}
      titleStyle={combineStyles(
        p('savedAddressTitle', TEXT_PROPERTIES),
        isActive() && p('savedAddressTitleActive', TEXT_PROPERTIES),
      )}
      subtitle={getSubtitle()}
      subtitleStyle={combineStyles(p('savedAddressSubtitle', TEXT_PROPERTIES))}
      action={onAddressClicked}
      active={isActive()}
      leftIcon={() => <RedcatMapPinIcon size={32} color={iconColor} />}
      rightSlot={() => getRightIcon()}
    />
  );
};

export default AddressItem;
